import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { PageHeader } from "@/components/common/DashboardBits";
import PincodeInput from "@/components/common/PincodeInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Save, Loader2 } from "lucide-react";

export default function ProfilePage() {
  const { user } = useAuth();
  const { t } = useLang();
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.entity_id) return;
    api.get(`/executives/${user.entity_id}`).then((r) => setMe(r.data));
  }, [user?.entity_id]);

  if (!me) return <p className="text-slate-500">Loading…</p>;

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = (({ name, email, phone, home_address, working_location, working_radius_km, kyc_aadhar, kyc_pan, city, pincode }) =>
        ({ name, email, phone, home_address, working_location, working_radius_km, kyc_aadhar, kyc_pan, city, pincode }))(me);
      await api.put(`/executives/${user.entity_id}`, payload);
      toast.success("Saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title={t("profile")} subtitle="KYC & working details" />
      <Card className="border border-slate-200 max-w-3xl">
        <CardContent className="p-6">
          <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Name</Label><Input required value={me.name} onChange={(e) => setMe({ ...me, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input required value={me.email} onChange={(e) => setMe({ ...me, email: e.target.value })} /></div>
            <div><Label>Phone</Label><Input required value={me.phone} onChange={(e) => setMe({ ...me, phone: e.target.value })} /></div>
            <PincodeInput
              value={me.pincode || ""}
              onChange={(p) => setMe({ ...me, pincode: p })}
              onResolved={(r) => {
                if (r.isRajasthan) setMe({ ...me, pincode: r.pincode, city: r.city, working_location: r.district });
              }}
              testId="field-profile-pincode"
              label="Working Pincode"
            />
            <div><Label>City</Label><Input value={me.city || ""} readOnly className="bg-slate-50" /></div>
            <div><Label>{t("working_location")}</Label><Input value={me.working_location || ""} readOnly className="bg-slate-50" /></div>
            <div><Label>{t("working_radius")}</Label><Input type="number" value={me.working_radius_km} onChange={(e) => setMe({ ...me, working_radius_km: Number(e.target.value) })} /></div>
            <div className="md:col-span-2"><Label>{t("home_address")}</Label><Textarea required value={me.home_address} onChange={(e) => setMe({ ...me, home_address: e.target.value })} /></div>
            <div><Label>Aadhaar</Label><Input value={me.kyc_aadhar || ""} onChange={(e) => setMe({ ...me, kyc_aadhar: e.target.value })} /></div>
            <div><Label>PAN</Label><Input value={me.kyc_pan || ""} onChange={(e) => setMe({ ...me, kyc_pan: e.target.value })} /></div>
            <div className="md:col-span-2">
              <Button type="submit" disabled={busy} className="bg-slate-950 hover:bg-slate-800" data-testid="field-profile-save">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> {t("save")}</>}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
